import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import clamp from 'lodash/clamp'
import analyticsUtils from '../../helpers/analytics-utils'
import { throttle } from '../../helpers'
import CosmeticScrollbar from '../cosmetic-scrollbar'
import style from './index.scss'

const THROTTLE_SCROLL = 250

const setScrollWithoutTransition = (elem, scrollPos) => {
  // Temporarily disable smooth scrolling while setting the new scroll position
  elem.style.scrollBehavior = 'auto'
  elem.scrollLeft = scrollPos
  elem.style.scrollBehavior = ''
}

class HorizontalScrollWrapper extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showLeftElem: false,
      showRightElem: true,
      hasTrackedScroll: false,
      cosmeticScrollbarPos: props.useCosmeticScrollbar ? 0 : null,
    }
    this.lastScrollPos = 0 // Not stored in state to avoid re-renders while scrolling
    this.scrollNode = React.createRef()
  }

  componentDidMount() {
    this.handleScroll()
  }

  // One-off event
  trackScroll = () => {
    if (
      this.props.eventScrollThreshold !== undefined &&
      this.scrollNode.current.scrollLeft > this.props.eventScrollThreshold &&
      !this.state.hasTrackedScroll
    ) {
      const { eventCategory, eventAction } = this.props
      const hasAnalyticsEvent = Boolean(eventCategory && eventAction)
      if (hasAnalyticsEvent) {
        this.setState({ hasTrackedScroll: true })
        analyticsUtils.gaTagTracking({
          event: analyticsUtils.events.MODULE_IMPRESSION,
          eventCategory,
          eventAction,
        })
      }
    }
  }

  trackScrollDirection = direction => {
    const { eventCategory, eventAction } = this.props
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.MODULE_IMPRESSION,
      eventCategory,
      eventAction,
      eventLabel:
        direction === 'forward'
          ? analyticsUtils.labels.ARROW_RIGHT
          : analyticsUtils.labels.ARROW_LEFT,
    })
  }

  getScroll() {
    return this.lastScrollPos
  }

  setScroll = (scrollPos, options = {}) => {
    const { immediate = false } = options
    const elem = this.scrollNode.current
    if (immediate) {
      setScrollWithoutTransition(elem, scrollPos)
    } else {
      elem.scrollLeft = scrollPos
    }
    this.handleScroll() // Setting scrollLeft doesn't always trigger the scroll event
  }

  handleScroll = () => {
    const elem = this.scrollNode.current
    if (!elem) {
      return
    }
    const containerWidth = elem.clientWidth
    const scrollLeft = elem.scrollLeft
    const contentWidth = elem.scrollWidth
    const showLeftElem = scrollLeft > 0
    const showRightElem = scrollLeft + containerWidth < contentWidth
    this.lastScrollPos = scrollLeft
    this.trackScroll()
    if (
      showLeftElem !== this.state.showLeftElem ||
      showRightElem !== this.state.showRightElem
    ) {
      this.setState({
        showLeftElem,
        showRightElem,
      })
    }

    if (this.props.useCosmeticScrollbar) {
      this.setCosmeticScrollbarPosition(
        scrollLeft,
        containerWidth,
        contentWidth
      )
    }
  }

  setCosmeticScrollbarPosition(scrollPos, visibleWidth, contentWidth) {
    const maxPosition = contentWidth - visibleWidth
    const positionAsPercentage = Math.round((scrollPos / maxPosition) * 100)
    /*
     * touch devices can make scroll requests beyond min/max
     * that 'snap' back when touch is released
     */
    const percentage = clamp(positionAsPercentage, 0, 100)

    this.setState({
      cosmeticScrollbarPos: percentage || 0,
    })
  }

  scrollPadding = () => {
    const elem = this.scrollNode.current
    if (!elem) {
      return 0
    }
    const elemStyle = window.getComputedStyle(elem)
    return (
      (parseFloat(elemStyle.paddingLeft) || 0) +
      (parseFloat(elemStyle.paddingRight) || 0)
    )
  }

  scrollPageSize = () => {
    const { extraScroll } = this.props
    const elem = this.scrollNode.current
    if (!elem) {
      return 0
    }
    const visibleWidth = elem.clientWidth - this.scrollPadding()
    const contentWidth = elem.scrollWidth
    const pages = contentWidth / visibleWidth
    return contentWidth / pages + extraScroll
  }

  moveScroll = direction => {
    const elem = this.scrollNode.current
    const sizePerPg = this.scrollPageSize()
    let scrollPos = elem.scrollLeft
    if (direction === 'forward') {
      scrollPos += sizePerPg
    } else {
      scrollPos -= sizePerPg
    }
    elem.scrollLeft = scrollPos
    this.trackScrollDirection(direction)
  }

  render() {
    const { cosmeticScrollbarPos, showLeftElem, showRightElem } = this.state
    const {
      bgColor,
      children,
      className,
      contentClassName,
      leftElem,
      rightElem,
      useCosmeticScrollbar,
    } = this.props
    const showCosmeticScrollbar =
      useCosmeticScrollbar && (showLeftElem || showRightElem)

    return (
      <Fragment>
        <div className={classNames(style.HorizontalScrollWrapper, className)}>
          {Boolean(leftElem) &&
            leftElem({
              isVisible: showLeftElem,
              onClick: () => this.moveScroll('back'),
            })}
          <div className={style.scrollContentWrapper}>
            <div
              ref={this.scrollNode}
              className={classNames(style.scrollContent, contentClassName)}
              onScroll={throttle(this.handleScroll, THROTTLE_SCROLL)}
            >
              {children}
            </div>
          </div>
          {Boolean(rightElem) &&
            rightElem({
              isVisible: showRightElem,
              onClick: () => this.moveScroll('forward'),
            })}
        </div>
        {showCosmeticScrollbar && (
          <div className={style.cosmeticWrapper}>
            <div className={style.cosmeticInner}>
              <CosmeticScrollbar
                lightTheme={bgColor === 'Seasonal'}
                position={cosmeticScrollbarPos}
              />
            </div>
          </div>
        )}
      </Fragment>
    )
  }
}

HorizontalScrollWrapper.propTypes = {
  bgColor: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  extraScroll: PropTypes.number,
  leftElem: PropTypes.func,
  rightElem: PropTypes.func,
  eventScrollThreshold: PropTypes.number,
  eventCategory: PropTypes.string,
  eventAction: PropTypes.string,
  useCosmeticScrollbar: PropTypes.bool,
}

HorizontalScrollWrapper.defaultProps = {
  extraScroll: 0,
  useCosmeticScrollbar: true,
}

export default HorizontalScrollWrapper
